export enum ClientError {
    INVALID_API_VERSION = 'Invalid API version',
    NOT_FOUND = 'Request not found',
    ENTITY_ERROR = 'Entity error',
    BAD_REQUEST = 'Bad request',
    REQUEST_FAILED = 'Request Failed',
    NO_CONNECTION = 'No connection',
    SERVER_ERROR = 'Server Error',
    INTERNAL_ERROR = 'Internal error',
    GENERAL_ERROR = 'General Error',
    GENERAL_REQUEST_ERROR = 'General request error',
    AUTHENTICATION_ERROR = 'Authentication Error',
    GATE_WAY_ERROR = 'Gate way error',
    RECORD_CONFLICT = 'Record conflict'
}

export type IdentificationTypeName = 'OpenMRS Identification Number' |
    'Old Identification Number' |
    'National id' |
    'ARV Number' |
    'z_deprecated Pre ART Number (Old format)' |
    'VHW ID' |
    'District TB Number' |
    'Dummy ID' |
    'z_deprecated EID Number' |
    'Unknown ID' |
    'MDR-TB Program Identifier' |
    'KS Number' |
    'z_deprecated PART Number' |
    'Diabetes Number' |
    'LAB IDENTIFIER' |
    'DS Number' |
    'Filing number' |
    'Archived filing number' |
    'HCC Number' |
    'Radiology Study Number' |
    'IVR Access Code' |
    'Pre-ART Number' |
    'Serial Number' |
    'HTC Identifier' |
    'ANC Connect ID' |
    'FUCHIA ID' |
    'DDE person document ID' |
    'Malawi National ID' |
    'District IPT Number' |
    'Legacy ARV Number'

export type UserRoleName = 'Anonymous' |
    'Provider' |
    'System Developer' |
    'Accompagnateur' |
    'Accompagnateur Leader' |
    'Adults' |
    'Clinician' |
    'Counselor' |
    'Data Assistant' |
    'Data Element Contributor' |
    'Data Manager' |
    'Doctor' |
    'General Registration Clerk' |
    'HMIS lab order' |
    'HSA' |
    'Informatics Manager' |
    'Lab' |
    'Medical Assistant' |
    'Nurse' |
    'Paediatrics' |
    'Pharmacist' |
    'Program Manager' |
    'Registration Clerk' |
    'Social Worker' |
    'SPINE clinician' |
    'Superuser' |
    '"Superuser,Superuser,"' |
    'Supervisor' |
    'Therapeutic Feeding Clerk' |
    'Vitals Clerk' |
    'Authenticated'

export interface UpdatedUser {
    password: string;
    given_name: string;
    family_name: string;
    roles: UserRoleName[];
}

export interface NewUser {
    given_name: string;
    family_name: string;
    roles: UserRoleName[];
    username: string;
    password: string;
}

export interface RequestData {
    url: string;
    config: Record<string, any>
}

export interface User {
    deactivated_on: string | null;
    user_id: number;
    creator: number;
    person_id: number;
    changed_by: number;
    date_changed: string;
    system_id: string;
    username: string;
    date_created: string;
    retired: number;
    retired_by: number | null;
    date_retired: string | null;
    retire_reason: string;
    uuid: string;
    roles: Role[];
    programs: any[]; // You might want to define a type for programs if needed
    person: Person;
}
  
export interface Role {
    role: string;
    description: string;
    uuid: string;
}
  
export interface Person {
    person_id: number;
    gender: string;
    birthdate: string | null;
    birthdate_estimated: number;
    dead: number;
    death_date: string | null;
    cause_of_death: string | null;
    creator: number;
    date_created: string;
    changed_by: number;
    date_changed: string;
    voided: number;
    voided_by: number | null;
    date_voided: string | null;
    void_reason: string | null;
    uuid: string;
    names: PersonName[];
}

export interface PersonName {
    person_name_id: number;
    preferred: number;
    person_id: number;
    prefix: string | null;
    given_name: string;
    middle_name: string;
    family_name_prefix: string | null;
    family_name: string;
    family_name2: string | null;
    family_name_suffix: string | null;
    degree: string | null;
    creator: number;
    date_created: string;
    voided: number;
    voided_by: number | null;
    date_voided: string | null;
    void_reason: string | null;
    changed_by: number | null;
    date_changed: string | null;
    uuid: string;
}

export interface RequestBody {
    method: 'GET' | 'POST' | 'PUT' | 'DELETE',
    body?: string
}

export interface AuthorizationData {
    token: string;
    expiry_time: string;
    user: User;
}

export interface DDESearchDemographics {
    given_name: string;
    family_name: string;
    gender: string;
}

export interface DDEclientMergeData {
    patient_id: number;
    doc_id: string;
}

/**
 * Data that is saved in session storage for persitence
 */ 
export interface ApiData {
    host: string;
    token: string;
    tokenExpiryDate: string;
    userID: string;
    username: string;
    userRoles: string;
}

/**
 * When making API calls, this object will be returns
 */
export interface JsonRequestResponse {
    /**
     * For successful API connections, ok will be true
     */
    ok: boolean;
    /**
     * Tracks error type for easy reference
     */
    errorState?: ClientError;
    /**
     * Any data returned by endpoint relating to the error occured
     */
    errorBody?: any;
    /**
     * Any data returned by the API
     */
    data?: any;
}

export interface ObsValue {
    concept_id: number;
    value_text?: string;
    value_coded?: number;
    value_datetime?: string;
    value_modifier?: string | null;
    value_boolean?: string;
    value_numeric?: number;
    obs_group_id?: number;
    obs_datetime?: string;
}

export interface NewObs {
    encounter_id: number;
    observations: Array<ObsValue>;
}

export interface NewPatient {
    given_name?: string;
    family_name?: string;
    gender?: string;
    birth_date?: string;
    home_district?: number;
    birthdate_estimated?: boolean;
    home_traditional_authority?: number;
    home_village?: number;
    current_district?: number;
    current_village?: number;
    current_traditional_authority?: number;
    cell_phone_number?: string;
    landmark?: string;
}

export interface NewPersonAttribute {
    person_id: number;
    person_attribute_type_id: number;
    value: number | string;
}

export interface PersonAttributeType {
    person_attribute_type_id: number;
    name: string;
    description: string;
    format: string;
    foreign_key: null;
    searchable:number;
    creator: number;
    date_created: Date;
    changed_by: number;
    date_changed: Date;
    retired: number;
    retired_by: null;
    date_retired: null;
    retire_reason: null;
    edit_privilege:null;
    uuid: string;
    sort_weight: number;
}

export interface PersonAttribute {
    person_attribute_id?: number;
    person_id: number;
    value: string;
    person_attribute_type_id: number;
    creator?: number;
    date_created?: Date;
    changed_by?: null;
    date_changed?: null;
    voided?: number;
    voided_by?: null;
    date_voided?: null;
    void_reason?: null;
    uuid?: string;
    type?: PersonAttributeType;
}

export interface NewPerson {
    person_id?: number;
    given_name: string;
    family_name: string;
    gender: string;
    birthdate: string;
    birthdate_estimated: boolean;
    home_district: string;
    home_traditional_authority: string;
    home_village: string;
    current_district: string;
    current_traditional_authority: string;
    current_village: string;
    landmark: string;
    cell_phone_number: string;
    occupation: string;
    facility_name: string;
    patient_type?: string;
    relationship?: string;
}

export interface NewState {
    state: number;
    date: string;
}

export interface NewTests {
    concept_id: number
}

export interface NewOrder {
    accession_number : string;
    encounter_id: number;
    tests: NewTests[];
    reason_for_test_id: string;
    target_lab: string;
    date: string;
    requesting_clinician: string;
    specimen?: {
        concept_id: number
    }
}

export interface NewLabOrder {
    encounter_id: number;
    order: NewOrder[];
}

export interface Drug{
    drug_inventory_id: number;
    dose: number;
    drug?: any;
    dosage_struct?: any;
    order?: any;
    equivalent_daily_dose: number;
    frequency: string;
    start_date: string;
    auto_expire_date: string;
    instructions: string;
    units: string;
    quantity?: number;
}

export interface NewDrugOrder {
    encounter_id: number;
    drug_orders: Drug[];
}
